
import React, { useState, useEffect } from 'react';
import { Badge } from '../components/UI';
import { apiRequest } from '../utils/api';
import { useAuth } from '../components/AuthContext';
import { UserRole } from '../types';
import { Navigate } from 'react-router-dom';
import { Users, RefreshCw } from 'lucide-react';
import { PageHero } from '../components/PageHero';

interface ReserveStat {
    user_id: string; username: string; ea_id: string; team_name: string; team_color: string; has_discord: boolean;
    times_registered: number; times_raced: number; not_selected: number; no_shows: number;
    availability: number; participation_rate: number;
    penalty_points: number; total_points: number; dnfs: number;
    game_pen_seconds: number; game_pen_count: number;
    best_position: number | null; avg_position: number | null;
}

export const ReserveStats: React.FC = () => {
    const { user } = useAuth();
    const [stats, setStats] = useState<ReserveStat[]>([]);
    const [totalEvents, setTotalEvents] = useState(0);
    const [loading, setLoading] = useState(true);
    const [sortField, setSortField] = useState<string>('availability');
    const [sortDir, setSortDir] = useState<'asc'|'desc'>('desc');

    if (!user || !user.roles.some((r: string) => [UserRole.ADMIN, UserRole.WEBADMIN, 'STEWARD'].includes(r as any))) return <Navigate to="/" />;

    useEffect(() => {
        setLoading(true);
        apiRequest<any>('reserve_stats.php').then(r => {
            setStats(Array.isArray(r) ? r : []);
            setTotalEvents(r?.total_events || 0);
        }).catch(() => setStats([])).finally(() => setLoading(false));
    }, []);

    const handleSort = (field: string) => {
        if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDir(field === 'username' ? 'asc' : 'desc'); }
    };

    const sorted = [...stats].sort((a: any, b: any) => {
        const va = a[sortField] ?? 0, vb = b[sortField] ?? 0;
        if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });

    const barColor = (v: number) => v >= 80 ? 'bg-green-500' : v >= 50 ? 'bg-yellow-500' : v >= 25 ? 'bg-orange-500' : 'bg-red-500';
    const textColor = (v: number) => v >= 80 ? 'text-green-400' : v >= 50 ? 'text-yellow-400' : v >= 25 ? 'text-orange-400' : 'text-red-400';

    const SH: React.FC<{field: string, children: React.ReactNode}> = ({field, children}) => (
        <th className="px-3 py-3 cursor-pointer hover:text-gray-400 transition-colors select-none whitespace-nowrap" onClick={() => handleSort(field)}>
            <span className="flex items-center gap-1">{children}{sortField === field && <span className="text-f1-red text-[10px]">{sortDir === 'asc' ? '↑' : '↓'}</span>}</span>
        </th>
    );

    if (loading) return <div className="py-20 text-center"><RefreshCw className="animate-spin mx-auto text-gray-600"/></div>;

    return (
        <div className="max-w-7xl mx-auto space-y-6 animate-fade-in pb-20">
            <PageHero
                eyebrow="Kader-Intelligence"
                title="Ersatzfahrer"
                description="Verfügbarkeit, Einsätze und Performance des Reservepools als belastbare Grundlage für die Rennplanung."
                icon={Users}
                accent="#f97316"
                stats={[
                    { label: 'Ersatzfahrer', value: stats.length },
                    { label: 'Rennen', value: totalEvents },
                    { label: 'Ø Verfügbarkeit', value: stats.length ? `${Math.round(stats.reduce((sum, item) => sum + item.availability, 0) / stats.length)}%` : '–' },
                ]}
            />

            {stats.length === 0 ? (
                <div className="rounded-[1.75rem] border border-dashed border-white/[0.075] p-12 text-center text-sm text-gray-600">Keine Ersatzfahrer in dieser Saison.</div>
            ) : (
                <div className="overflow-hidden rounded-[1.75rem] border border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017] shadow-[0_24px_70px_rgba(0,0,0,0.23)]">
                    <div className="h-[2px] bg-gradient-to-r from-orange-500 via-orange-500/50 to-transparent"/>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead><tr className="bg-[#0e0e16] text-[8px] font-bold text-gray-600 uppercase tracking-wider">
                                <SH field="username">Fahrer</SH>
                                <SH field="availability">Verfügbarkeit</SH>
                                <SH field="times_registered">Angemeldet</SH>
                                <SH field="times_raced">Gefahren</SH>
                                <SH field="not_selected">Nicht berücksichtigt</SH>
                                <SH field="total_points">Punkte</SH>
                                <SH field="avg_position">Ø Pos</SH>
                                <SH field="best_position">Best</SH>
                                <SH field="dnfs">DNFs</SH>
                                <SH field="penalty_points">Strafpkt.</SH>
                                <SH field="game_pen_seconds">Zeitstrafen</SH>
                            </tr></thead>
                            <tbody>
                                {sorted.map(s => (
                                    <tr key={s.user_id} className="border-t border-gray-800/10 hover:bg-white/[0.02] transition-colors">
                                        <td className="px-3 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-1 h-6 rounded-full" style={{backgroundColor: s.team_color}}/>
                                                <div>
                                                    <div className="text-xs font-bold text-white">{s.username}</div>
                                                    <div className="text-[9px] text-gray-600">{s.team_name}{s.has_discord && <span className="ml-1 text-blue-400">@</span>}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-3 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-16 h-1.5 bg-gray-800 rounded-full overflow-hidden"><div className={`h-full rounded-full ${barColor(s.availability)}`} style={{width:`${s.availability}%`}}/></div>
                                                <span className={`font-mono font-bold text-xs ${textColor(s.availability)}`}>{s.availability}%</span>
                                            </div>
                                            <div className="text-[8px] text-gray-700 mt-0.5">{s.times_registered}/{totalEvents} Events</div>
                                        </td>
                                        <td className="px-3 py-3 font-mono text-xs text-gray-400 text-center">{s.times_registered}</td>
                                        <td className="px-3 py-3 font-mono text-xs text-white font-bold text-center">{s.times_raced}</td>
                                        <td className="px-3 py-3 text-center"><span className="font-mono text-xs text-gray-500">{s.not_selected}</span></td>
                                        <td className="px-3 py-3 font-mono text-xs text-white font-bold text-center">{s.total_points}</td>
                                        <td className="px-3 py-3 font-mono text-xs text-gray-400 text-center">{s.avg_position ?? '-'}</td>
                                        <td className="px-3 py-3 text-center"><span className={`font-mono text-xs font-bold ${s.best_position && s.best_position <= 3 ? 'text-yellow-400' : 'text-gray-400'}`}>{s.best_position ? `P${s.best_position}` : '-'}</span></td>
                                        <td className="px-3 py-3 text-center"><span className={`font-mono text-xs font-bold ${s.dnfs > 0 ? 'text-orange-400' : 'text-green-500'}`}>{s.dnfs}</span></td>
                                        <td className="px-3 py-3 text-center"><span className={`font-mono text-xs font-bold ${s.penalty_points > 0 ? 'text-red-400' : 'text-green-500'}`}>{s.penalty_points}</span></td>
                                        <td className="px-3 py-3 text-center">
                                            {s.game_pen_seconds > 0 ? <span className="text-red-400 text-[10px] font-bold">{s.game_pen_seconds}s <span className="text-gray-600">({s.game_pen_count}x)</span></span> : <span className="text-green-500 text-[10px]">Clean</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};
